import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FileText, Scale, AlertTriangle, Shield, Users, Building2,
  Phone, Mail, ArrowRight, AlertCircle, Clock, Gavel
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const TermsPage = () => {
  const { t } = useTranslation();

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Terms of Service", path: "/terms" }
  ];

  return (
    <div className="min-h-screen bg-offwhite" data-testid="terms-page">
      <SEO 
        titleKey="seo.terms.title"
        descriptionKey="seo.terms.description"
        path="/terms"
        breadcrumbs={breadcrumbs}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-navy-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gold/20 rounded-xl flex items-center justify-center">
                <Scale className="w-6 h-6 text-gold" />
              </div>
              <span className="text-gold font-semibold uppercase tracking-wider text-sm">
                {t('legal.badge')}
              </span>
            </div>
            <h1 className="font-space text-4xl md:text-5xl font-bold text-white mb-4">
              {t('legal.terms.title')}
            </h1>
            <p className="text-platinum-light text-lg">
              {t('legal.terms.subtitle')}
            </p>
            <p className="text-platinum-light/70 text-sm mt-4">
              {t('legal.terms.lastUpdated')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            
            {/* Important Notice */}
            <Card className="mb-6 border-2 border-gold/30 bg-gold/5">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.terms.importantNotice.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm leading-relaxed">
                  {t('legal.terms.importantNotice.content')}
                </p>
              </CardContent>
            </Card>

            {/* Definitions */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.terms.sections.definitions.title')}</h2>
                </div>
                <ul className="space-y-4 text-sm text-slate-700">
                  {['coordinator', 'client', 'coordinationServices', 'thirdParty', 'scope'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                      <span>{t(`legal.terms.sections.definitions.items.${item}`)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Nature of Services */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.terms.sections.nature.title')}</h2>
                </div>
                <ul className="space-y-4 text-sm text-slate-700">
                  {['role', 'services', 'authorization', 'disclaimer'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                      <span>{t(`legal.terms.sections.nature.items.${item}`)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Quotations and Payment */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.terms.sections.quotations.title')}</h2>
                </div>
                <ul className="space-y-4 text-sm text-slate-700">
                  {['validity', 'agreement', 'paymentTerms', 'latePayment', 'lien'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                      <span>{t(`legal.terms.sections.quotations.items.${item}`)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Liability */}
            <Card className="mb-6 border-2 border-navy-900/20 bg-slate-50">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.terms.sections.liability.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm mb-4">{t('legal.terms.sections.liability.intro')}</p>
                <ul className="space-y-4 text-sm text-slate-700">
                  {['faultStandard', 'monetaryCap', 'perKgLimit', 'invoiceCeiling', 'excludedLosses', 'thirdPartyLiability'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                      <span>{t(`legal.terms.sections.liability.items.${item}`)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Client Obligations */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.terms.sections.clientObligations.title')}</h2>
                </div>
                <ul className="space-y-4 text-sm text-slate-700">
                  {['information', 'packaging', 'documentation', 'warranty', 'dangerousGoods'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                      <span>{t(`legal.terms.sections.clientObligations.items.${item}`)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Insurance */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.terms.sections.insurance.title')}</h2>
                </div>
                <ul className="space-y-4 text-sm text-slate-700">
                  {['noInsurance', 'advice', 'intermediary'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                      <span>{t(`legal.terms.sections.insurance.items.${item}`)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Claims */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.terms.sections.claims.title')}</h2>
                </div>
                <ul className="space-y-4 text-sm text-slate-700">
                  {['visibleDamage', 'concealedDamage', 'claimSubmission', 'legalProceedings', 'cooperation'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                      <span>{t(`legal.terms.sections.claims.items.${item}`)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Force Majeure */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.terms.sections.forceMajeure.title')}</h2>
                </div>
                <ul className="space-y-4 text-sm text-slate-700">
                  {['definition', 'suspension'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                      <span>{t(`legal.terms.sections.forceMajeure.items.${item}`)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Governing Law */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Gavel className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.terms.sections.governingLaw.title')}</h2>
                </div>
                <ul className="space-y-4 text-sm text-slate-700">
                  {['law', 'jurisdiction', 'conventions'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                      <span>{t(`legal.terms.sections.governingLaw.items.${item}`)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Company Info */}
            <Card className="mb-6 border border-platinum-light bg-navy-900">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-white">{t('legal.terms.sections.companyInfo.title')}</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/10 p-4 rounded-lg">
                    <p className="text-white font-semibold mb-2">{t('legal.companyName')}</p>
                    <p className="text-platinum-light text-sm">{t('legal.companyLocation')}</p>
                    <p className="text-platinum-light text-sm">KvK: 42041124</p>
                    <p className="text-platinum-light text-sm">EORI: NL3785680245</p>
                    <p className="text-platinum-light text-sm">VAT: NL004527618B01</p>
                  </div>
                  <div className="bg-white/10 p-4 rounded-lg">
                    <p className="text-white font-semibold mb-2">{t('legal.terms.sections.companyInfo.contact')}</p>
                    <p className="text-platinum-light text-sm">
                      <a href="mailto:info@elementarycargo.com" className="hover:text-gold">info@elementarycargo.com</a>
                    </p>
                    <p className="text-platinum-light text-sm">
                      <a href="tel:+31625381824" className="hover:text-gold">+31 62 538 1824</a>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Other Legal Documents */}
            <Card className="border border-platinum-light">
              <CardContent className="p-8">
                <h3 className="font-space text-lg font-bold text-navy-900 mb-4">Related Documents</h3>
                <div className="flex flex-wrap gap-4">
                  <Link to="/privacy">
                    <Button variant="outline" className="border-2 border-navy-900/20">
                      {t('legal.viewPrivacy')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/cookies">
                    <Button variant="outline" className="border-2 border-navy-900/20">
                      {t('legal.viewCookies')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
